import { makeAutoObservable } from 'mobx';
import { z } from 'zod';
import { ItemWithLabel } from '../../../../../../components';
import {
  genId,
  INVALID_VERSION_MESSAGE,
  StringDto,
  validateVersion,
} from '../../../../../../store/utils/Dto';
import extractValue, {
  EventOrValue,
} from '../../../../../../store/utils/extractValue';
import Validator from '../../../../../../store/utils/Validator';
import { errorHandler } from '../../../../../../utils/errors';
import { isKnownVariable } from './envVariables';
import RuleExplanation from './RuleExplanation';

const OS_LIST = ['darwin', 'linux', 'win32'] as const;
export const OS_LABELS: ItemWithLabel<RuleOs>[] = [
  { value: 'win32', label: 'Windows' },
  { value: 'darwin', label: 'macOS' },
  { value: 'linux', label: 'Linux' },
];

const TYPE_LIST = ['copy', 'move', 'delete'] as const;
export const TYPE_LABELS: ItemWithLabel<RuleType>[] = [
  { value: 'copy', label: 'Copy' },
  { value: 'move', label: 'Move' },
  { value: 'delete', label: 'Delete' },
];

const ON_ERROR_LIST = ['ignore', 'notify', 'terminate'] as const;
export const ON_ERROR_LABELS: ItemWithLabel<RuleOnError>[] = [
  { value: 'ignore', label: 'Ignore' },
  { value: 'notify', label: 'Notify' },
  { value: 'terminate', label: 'Terminate' },
];

export default class Rule {
  static readonly OsLabels = OS_LABELS;
  static readonly TypeLabels = TYPE_LABELS;
  static readonly OnErrorLabels = ON_ERROR_LABELS;

  static readonly Schema = z.object({
    sourcePath: z.string().min(1),
    destinationPath: z.string().superRefine((value, ctx) => {
      const message = validateDestinationPath(value);
      if (message) {
        ctx.addIssue({ code: 'custom', message });
      }
    }),
    os: z.array(z.enum(OS_LIST)).min(1),
    type: z.enum(TYPE_LIST),
    onError: z.enum(ON_ERROR_LIST),
    version: z
      .string()
      .refine(
        (value) => !value || validateVersion(value),
        INVALID_VERSION_MESSAGE,
      ),
  });

  sourcePath = '';
  destinationPath = '';
  os: RuleOs[];
  type: RuleType;
  version: string;
  onError: RuleOnError = 'ignore';

  readonly id = genId();
  isDeleted = false;

  private readonly validator = new Validator(this, Rule.Schema);

  constructor(
    dto: RuleDto,
    private readonly onSave: (rule: Rule) => Promise<void>,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.sourcePath = dto.sourcePath || '';
    this.destinationPath = dto.destinationPath || '';
    this.onError = dto.onError || 'ignore';
    this.type = dto.type || 'copy';
    this.version = dto.version || '';

    // eslint-disable-next-line unicorn/prefer-ternary
    if (typeof dto.os === 'string') {
      this.os = [dto.os];
    } else {
      this.os = dto.os || ['darwin', 'linux', 'win32'];
    }
  }

  get allErrorsAsStrings(): string[] {
    return this.validator.allErrorsAsStrings;
  }

  get errors(): StringDto<RuleDto> {
    return this.validator.errorsForEnabledFields;
  }

  get explanation(): RuleExplanation {
    return RuleExplanation.fromRule(this);
  }

  get isValid() {
    const { destinationPath, ...errors } = this.validator.allErrors;
    return Object.keys(errors).length === 0;
  }

  addVariable(varName: string) {
    this.destinationPath += `{${varName}}`;
    this.validator.validate();
  }

  handleChange(e: EventOrValue<keyof Rule>) {
    const { name, value } = extractValue(e);
    Object.assign(this, { [name]: value });
    this.validator.validate();
  }

  handleBlur(e: EventOrValue<keyof Rule>) {
    const { name } = extractValue(e);
    this.validator.enableFieldValidation(name);
  }

  hasOs(os: RuleOs): boolean {
    return this.os.includes(os);
  }

  validate(): string[] {
    this.validator.validate();
    return this.validator.allErrorsAsStrings;
  }

  async save() {
    this.onSave(this).catch(errorHandler('Cannot save rule'));
  }

  async delete() {
    this.isDeleted = true;
    await this.onSave(this).catch(errorHandler('Cannot delete rule'));
  }

  toJSON(): Required<RuleDto> {
    return {
      sourcePath: this.sourcePath,
      destinationPath: this.type === 'delete' ? '' : this.destinationPath,
      onError: this.onError,
      os: this.os,
      type: this.type,
      version: this.version,
    };
  }
}

function validateDestinationPath(destPath: string): string | undefined {
  const templates = (destPath || '').match(/{(.*?)}/g) || [];
  const variables = templates.map((tpl) => tpl.replaceAll(/(^{|}$)/g, ''));

  for (const variable of variables) {
    if (!isKnownVariable(variable)) {
      return (
        `Unknown environment variable ${variable}. Please make sure it's ` +
        'defined on a customer PC, otherwise the install fails'
      );
    }
  }

  return undefined;
}

export interface RuleDto {
  destinationPath?: string;
  onError?: RuleOnError;
  os?: RuleOs[];
  sourcePath?: string;
  type?: RuleType;
  version?: string;
}

export type RuleOs = (typeof OS_LIST)[number];
export type RuleType = (typeof TYPE_LIST)[number];
export type RuleOnError = (typeof ON_ERROR_LIST)[number];
